import { AlertFactory } from 'alerts/Alert'
import { GetCurrentMember } from 'iam/SessionStore'
import JobStore from 'jobs/JobStore'
import { Id } from 'types/definitions'
import { ignore404 } from 'utils/error-util'
import Document from './Document'
import DocumentAccessStore from './DocumentAccessStore'
import DocumentStore from './DocumentStore'
import DocumentVersion from './DocumentVersion'
import DocumentVersionStore from './DocumentVersionStore'
import {
  AssignDocumentTypePayload,
  UpdateDocumentPayload,
} from './dtos/document'
import { DocumentDetailsModalPorts } from './views/DocumentDetailsModal/ports'
import DocumentTypeStore from './DocumentTypeStore'

export class DocumentDetailsAdapter implements DocumentDetailsModalPorts {
  // Bound ports
  fetchTypes = this.documentTypeStore.fetchTypes.bind(this.documentTypeStore)
  documentTypes = this.documentTypeStore.types
  updateAccess = this.documentAccessStore.updateAccess.bind(
    this.documentAccessStore,
  )

  deleteAccess = this.documentAccessStore.deleteAccess.bind(
    this.documentAccessStore,
  )

  constructor(
    public getCurrentMember: GetCurrentMember,
    private documentTypeStore: DocumentTypeStore,
    private documentStore: DocumentStore,
    private documentAccessStore: DocumentAccessStore,
    private documentVersionStore: DocumentVersionStore,
    private jobStore: JobStore,
    public newAlert: AlertFactory,
  ) {}

  downloadDocument(document: Document) {
    this.documentStore.download(document, false)
  }

  downloadDocumentVersion(
    document: Document,
    documentVersion: DocumentVersion,
  ) {
    this.documentStore.download(
      document,
      false,
      documentVersion,
      documentVersion.files[0],
    )
  }

  async fetchAccessInfo(document: Document) {
    // In order to show accesses, we need to fetch the related info.
    // We can assume that if the access exists on the document, that we have
    // permission to pull the underlying resource tied to it.
    await Promise.all(
      document.accesses.map(async (access) =>
        access.type === 'Job'
          ? await this.jobStore.fetchJob(access.jobId).catch(ignore404)
          : null,
      ),
    ).catch((err) => console.error(err))
  }

  updateDocument(document: Document, data: UpdateDocumentPayload) {
    return this.documentStore.updateDocument(Id.assert(document.longId), data)
  }

  archiveDocument(document: Document) {
    return this.documentStore.archiveDocument(Id.assert(document.longId))
  }

  assignType(document: Document, data: AssignDocumentTypePayload) {
    return this.documentStore.assignType(Id.assert(document.id), data)
  }

  createVersion(document: Document, file: File, message: string) {
    return this.documentVersionStore.createVersion({
      message,
      document_long_id: Id.assert(document.longId),
      action: {
        type: 'ReplaceContents',
        files: [file],
      },
    })
  }

  revertToVersion(
    document: Document,
    targetVersion: DocumentVersion,
    message: string,
  ) {
    return this.documentVersionStore.createVersion({
      action: {
        type: 'RevertToVersion',
        version_id: Id.assert(targetVersion.id),
      },
      message,
      document_long_id: Id.assert(document.longId),
    })
  }
}
